import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { HandCombosContext, PlayerSelectedContext } from "../context/store";
import {
  ActionTypes,
  FONT_FAMILY,
  PRESET_HANDS,
  PRESET_BOX_CLASS,
  PRESET_MODAL_CLASS,
  ZINDEXES
} from "../constants";
import InstructionsIcon from "./icons/Instructions";
import HomeIcon from "./icons/Home";
import TwitterIcon from "./icons/Twitter";
import PresetsIcon from "./icons/Presets";
import CardsIcon from "./icons/Cards";
import EmailSubmit from "./EmailSubmit";

const ACTIVE_TEXT = "#ffffff";
const IDLE_TEXT = "#aebac1";

function rowStyle(active) {
  return {
    display: "flex",
    alignItems: "center",
    height: "38px",
    margin: "0 6px",
    padding: "0 9px",
    borderLeft: `3px solid ${active ? "#6fbcff" : "transparent"}`,
    borderRadius: "5px",
    background: active ? "#33454e" : "transparent",
    color: active ? ACTIVE_TEXT : IDLE_TEXT,
    textDecoration: "none",
    cursor: "pointer"
  };
}

function RowBody(props) {
  const { icon: Icon, label, active } = props;
  return (
    <>
      <Icon size={20} fill={active ? ACTIVE_TEXT : IDLE_TEXT} />
      <span style={{ marginLeft: "10px" }}>{label}</span>
    </>
  );
}

function NavRow(props) {
  const { to, icon, label, active, title } = props;
  return (
    <Link to={to} style={rowStyle(active)} title={title}>
      <RowBody icon={icon} label={label} active={active} />
    </Link>
  );
}

function TwitterLink(props) {
  return (
    <a
      href="http://twitter.com/handcombos"
      style={rowStyle(false)}
      title="@handcombos on Twitter"
    >
      <RowBody icon={TwitterIcon} label="@handcombos" />
    </a>
  );
}
function PresetsModal(props) {
  const { onClose } = props;
  const [, handleSelectedDispatch] = useContext(HandCombosContext);
  const [playerSelected] = useContext(PlayerSelectedContext);

  // Close on Escape, the way a modal is expected to behave.
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  function choosePreset(preset) {
    handleSelectedDispatch({
      type: ActionTypes.REPLACE_HAND_COMBOS,
      player: playerSelected,
      newCombos: PRESET_HANDS[preset]
    });
    onClose();
  }

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, .55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: ZINDEXES.modal
  };
  const modalStyle = {
    background: "#273238",
    color: "white",
    borderRadius: "6px",
    padding: "1.25rem",
    width: "min(560px, 90vw)",
    maxHeight: "80vh",
    overflowY: "auto",
    fontFamily: FONT_FAMILY,
    boxShadow: "0 16px 24px 2px rgba(0, 0, 0, .4)"
  };
  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: "1rem",
    fontWeight: "600"
  };
  const closeStyle = {
    background: "none",
    border: "none",
    color: IDLE_TEXT,
    fontSize: "1.4rem",
    lineHeight: "1",
    cursor: "pointer",
    padding: "0 .25rem"
  };
  const blurbStyle = {
    color: IDLE_TEXT,
    fontSize: ".75rem",
    fontWeight: "400",
    margin: ".4rem 0 0 0"
  };
  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gridGap: ".75rem",
    marginTop: "1rem"
  };
  const boxStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    minHeight: "90px",
    padding: ".75rem",
    background: "#31404a",
    border: "1px solid #3b4a52",
    borderRadius: "5px",
    color: "white",
    fontFamily: FONT_FAMILY,
    fontSize: ".8rem",
    fontWeight: "600",
    cursor: "pointer"
  };

  return (
    <div className={PRESET_MODAL_CLASS} style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={headerStyle}>
          <span>Hand range presets</span>
          <button style={closeStyle} onClick={onClose} title="Close">
            &times;
          </button>
        </div>
        <p style={blurbStyle}>
          Choose a preset to replace the selected player's range.
        </p>
        <div style={gridStyle}>
          {Object.keys(PRESET_HANDS).map(preset => (
            <div
              key={preset}
              className={PRESET_BOX_CLASS}
              style={boxStyle}
              onClick={() => choosePreset(preset)}
            >
              {preset}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SelectPresetAction() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <div
        onClick={() => setModalOpen(true)}
        style={rowStyle(false)}
        title="Hand range presets"
      >
        <RowBody icon={PresetsIcon} label="Presets" />
      </div>
      {modalOpen ? <PresetsModal onClose={() => setModalOpen(false)} /> : null}
    </>
  );
}

export function LeftSideBar(props) {
  const { pathname } = useLocation();
  const style = {
    background: "#273238",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    color: "white",
    textDecoration: "none",
    fontFamily: FONT_FAMILY,
    fontSize: ".85rem",
    fontWeight: "600"
  };
  const wordmarkStyle = {
    // Matches the navbar row so the two line up across the top of the grid.
    height: "76px",
    flex: "0 0 76px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#1f2122",
    color: "white",
    textDecoration: "none",
    fontSize: "1.05rem"
  };
  const ruleStyle = {
    height: "1px",
    background: "#3b4a52",
    margin: ".75rem 14px .5rem 14px"
  };
  const sectionLabelStyle = {
    padding: "0 18px",
    marginBottom: ".35rem",
    color: "#7b8b93",
    fontSize: ".65rem",
    letterSpacing: ".08em"
  };
  return (
    <div style={style}>
      <Link to="/" style={wordmarkStyle}>
        HandCombos.com
      </Link>
      <NavRow
        to="/"
        icon={HomeIcon}
        label="Calculator"
        active={pathname === "/"}
        title="Calculator Home"
      />
      <NavRow
        to="/omaha"
        icon={CardsIcon}
        label="Omaha"
        active={pathname === "/omaha"}
        title="Omaha calculator"
      />
      <NavRow
        to="/guide"
        icon={InstructionsIcon}
        label="Guide"
        active={pathname === "/guide"}
        title="Instructions"
      />
      <div style={ruleStyle} />
      <div style={sectionLabelStyle}>TOOLS</div>
      <SelectPresetAction />
      <div style={{ padding: ".75rem 10px 0 10px" }}>
        <EmailSubmit variant="stacked" />
      </div>
      {/* Only the social link is pinned; the signup follows the nav directly */}
      <div style={{ flex: "1 1 auto" }} />
      <div style={{ padding: ".5rem 0 .75rem 0" }}>
        <TwitterLink />
      </div>
    </div>
  );
}
